import { FC, useMemo } from 'react';
import React from 'react'; // Ensure React is imported for JSX typing
import Typography from 'components/commercetools-ui/atoms/typography';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import ContentSliderSlide from './slide';
import { ContentSliderProps, ContentSliderSlideProps } from './types';
import Wrapper from '../../../HOC/wrapper';
import Slider from '../../atoms/slider';
import Subtitle from '../../atoms/subtitle';
import { useParams } from 'next/navigation';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Link from 'components/commercetools-ui/atoms/link';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';


const ContentSlider: FC<ContentSliderProps> = ({ data }) => {
  const slideCount = data?.card_header_section?.length || 0;

  const [windowWidth] = useMediaQuery(); // assuming this returns a single value
  const showSliderWidth = slideCount === 2 ? 642 : 1024;  
  const showSlider = windowWidth <= showSliderWidth;
  const { isTouchDevice } = useTouchDevice();
  const {slug} = useParams();
  const isWarranty = slug?.includes('caroma-warranties');


  let overrideContainerStyle = {};
  let overrideSlideGap = ''; 
  if (slideCount === 2) {    
    const maxWidth = showSlider || windowWidth > 1500 || windowWidth < 800 ? 1300 : windowWidth - 280;
    overrideContainerStyle = {
      maxWidth: `${maxWidth}px`,
    };
    overrideSlideGap = "md:gap-24 xl:gap-48 2xl:gap-64 3xl:gap-64";
  }

  let WrapperClass = isWarranty ? 
          "md:px-28 lg:px-80 pt-30 pb-32 xl:px-80 xl:pt-30 xl:pb-40 2xl:px-80 2xl:pt-30 2xl:pb-60 3xl:px-0"
          :
          "md:px-28 lg:px-80 pt-48 pb-32 xl:px-80 xl:pt-60 xl:pb-40 2xl:px-80 2xl:pt-80 2xl:pb-60 3xl:px-0";
  WrapperClass = showSlider ? `pl-24 ${WrapperClass}` : `px-28 ${WrapperClass}`;

  const slidesElement = useMemo(
    () => data?.card_header_section?.map((slide: JSX.IntrinsicAttributes & ContentSliderSlideProps, index: string | number | null | undefined) => (
      <ContentSliderSlide key={index} {...slide} />
    )),
    [data?.card_header_section],
  );

  return (
    <>
      {isWarranty && (
        <div className="hidden px-24 pb-8 pt-16 md:block md:px-12 lg:px-32 xl:px-44 2xl:px-64">
          <Breadcrumb Separator={<ChevronRightIcon className="mt-2 w-16" />} listClassName="!gap-x-16">
            <Link link={'/'}>
              <HomeIcon className="mt-6 w-20" />
            </Link>
            <span className="text-14 font-normal leading-4 text-[#666]">Warranties</span>
          </Breadcrumb>
        </div>
      )}
    

      <div className="our-product-container" style={overrideContainerStyle}>
        <Wrapper clearDefaultStyles className={WrapperClass}>
          {data?.header?.section_title && (
            <Typography className="mb-36 md:mb-48 text-32 xl:text-32 2xl:text-48 font-medium leading-8 xl:leading-[2.25rem] 2xl:leading-[3.25rem] text-[#090909]" as="h3">
              {data?.header?.section_title}
            </Typography>
          )}
          {data?.subtitle && <Subtitle className="mb-24" subtitle={data?.subtitle} />}
          {!showSlider ? (
            <div className={`flex w-full gap-24 ${overrideSlideGap}`}>{slidesElement}</div>
          ) : (
            <div className="relative w-full">
              <Slider
                dots={false}
                solidArrows
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                }}
                arrows={false}
                allowArrowsOnTouchDevice
                allowTouchMove={isTouchDevice}
                slideWidth={290}
                nextButtonStyles={{ transform: 'translateY(-150%)', right: '10px' }}
                prevButtonStyles={{ transform: 'translateY(-150%)', left: '10px' }}
                spaceBetween={16}
              >
                {slidesElement}
              </Slider>
            </div>
          )}
        </Wrapper>
      </div>
    </>
  );
};

export default ContentSlider;
export * from './types';
